import {
  Directive,
  ElementRef,
  HostAttributeToken,
  HostBinding,
  HostListener,
  inject,
} from "@angular/core";

/**
 * Keyboard support for a `routerLink` on something that isn't a link or button (e.g. a
 * `<nav-panel>` or a card `<div>`). RouterLink gives such a host a tabindex, so it's focusable,
 * but only listens for click: Enter did nothing. This makes Enter click it (which RouterLink then
 * handles) and exposes it as a link, like `<a href>` — so Space, as on a link, isn't handled.
 *
 * Prefer a real `<a [routerLink]>` where the content allows it; this covers hosts that can't be.
 */
@Directive({
  selector: "[routerLink]:not(a):not(button):not(area)",
})
export class RouterLinkKeyboardDirective {
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  /** An authored static role (e.g. role="button") wins; read once rather than back off the DOM
   * on every change-detection pass. */
  @HostBinding("attr.role")
  readonly role = inject(new HostAttributeToken("role"), { optional: true }) ?? "link";

  @HostListener("keydown.enter", ["$event"])
  onEnter(event: KeyboardEvent): void {
    // Only for Enter on the host itself, not bubbling up from a control inside it.
    if (event.target !== this.host.nativeElement || event.repeat) {
      return;
    }
    event.preventDefault();
    this.host.nativeElement.click();
  }
}
